import React, { useState, useEffect } from "react";
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  FilterOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Tabs, App, Select, Table, Modal, Button, Popconfirm } from "antd";
import * as XLSX from "xlsx";
import {
  fetchPCS,
  createPCS,
  deletePCS,
  uploadPcsOutputPower,
  fetchPCSOutputPowers,
  createPCSOutputPower,
  updatePCSOutputPower,
  deletePCSOutputPower,
  BASE_URL,
  customFetch,
} from "../../services/bessApi";

export default function PcsPage() {
  const { message } = App.useApp();
  const [pcsList, setPcsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPcs = async () => {
    try {
      setLoading(true);
      const data = await fetchPCS();
      setPcsList(data);
    } catch (err) {
      message.error("Failed to load PCS systems: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPcs();
  }, []);

  const tabItems = [
    {
      key: "pcs",
      label: <span className="font-semibold text-sm">PCS</span>,
      children: (
        <PcsTab
          pcsList={pcsList}
          loading={loading}
          onSuccess={loadPcs}
        />
      ),
    },
    {
      key: "pcs_output",
      label: <span className="font-semibold text-sm">PCS Output Powers</span>,
      children: (
        <PcsOutputTab
          pcsList={pcsList}
          loadingPcs={loading}
        />
      ),
    },
  ];

  return (
    <div className="bess-container-redesign min-h-screen p-6 flex flex-col">
      {/* Styles Block for Redesign overriding index.html global styles with higher specificity */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hanken+Grotesk:ital,wght@0,100..900;1,100..900&family=Geist:wght@100..900&family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

        .bess-container-redesign {
          background-color: #f7f9fb !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .bess-container-redesign h1,
        .bess-container-redesign h2,
        .bess-container-redesign h3,
        .bess-container-redesign h4 {
          font-family: 'Hanken Grotesk', sans-serif !important;
          font-weight: 600 !important;
          border-left: none !important;
          padding-left: 0 !important;
          text-transform: none !important;
          margin: 0 !important;
          letter-spacing: -0.01em !important;
        }

        .bess-container-redesign .headline-lg {
          font-size: 32px !important;
          line-height: 40px !important;
          letter-spacing: -0.02em !important;
        }

        .bess-container-redesign .headline-md {
          font-size: 24px !important;
          line-height: 32px !important;
        }

        .bess-container-redesign .headline-sm {
          font-size: 18px !important;
          line-height: 24px !important;
        }

        /* Labels override from global index.html style */
        .bess-layout-content .bess-container-redesign label {
          color: #3f4850 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          text-transform: none !important;
          letter-spacing: normal !important;
          margin-bottom: 6px !important;
          display: block !important;
        }

        /* Inputs/select overrides from global index.html style */
        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]),
        .bess-layout-content .bess-container-redesign select {
          height: 44px !important;
          padding: 0 14px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 400 !important;
          color: #191c1e !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          background-color: rgba(248, 250, 252, 0.5) !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
          box-sizing: border-box !important;
          width: 100% !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):hover,
        .bess-layout-content .bess-container-redesign select:hover {
          border-color: #707881 !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):focus,
        .bess-layout-content .bess-container-redesign select:focus {
          border-color: #006194 !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
          outline: none !important;
        }

        /* Premium Card style */
        .bess-card {
          background-color: #ffffff !important;
          border-radius: 10px !important; /* xl rounded corner */
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
          padding: 24px !important;
          border: 1px solid #eceef0 !important;
          box-sizing: border-box !important;
        }

        /* Style Ant Design Select dropdown to look incredibly premium with curves */
        .bess-container-redesign .bess-antd-select.ant-select .ant-select-selector {
          background-color: rgba(248, 250, 252, 0.5) !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
        }
        
        .bess-container-redesign .bess-antd-select.ant-select:hover .ant-select-selector {
          border-color: #707881 !important;
        }
        
        .bess-container-redesign .bess-antd-select.ant-select-focused .ant-select-selector {
          border-color: #006194 !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
        }

        .ant-select-dropdown {
          border-radius: 12px !important;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #f1f5f9 !important;
          padding: 6px !important;
          z-index: 10000 !important;
        }

        .ant-select-dropdown .ant-select-item {
          border-radius: 6px !important;
          padding: 8px 12px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .ant-select-dropdown .ant-select-item-option-selected {
          background-color: #f0f9ff !important;
          color: #006194 !important;
          font-weight: 600 !important;
        }

        .ant-select-dropdown .ant-select-item-option-active {
          background-color: #f8fafc !important;
        }

        /* AntD Tab styling overrides */
        .bess-container-redesign .premium-tabs {
          margin: 0 !important;
          padding: 0 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-nav {
          background-color: #f7f9fb !important;
          border-bottom: 1px solid #e0e3e5 !important;
          margin-bottom: 24px !important;
          padding: 0 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab {
          padding: 12px 16px !important;
          margin: 0 16px 0 0 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          color: #3f4850 !important;
          transition: all 0.2s !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab:hover {
          color: #006194 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab-active {
          color: #006194 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #006194 !important;
          font-weight: 600 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-ink-bar {
          background: #006194 !important;
          height: 2px !important;
        }

        /* Buttons */
        .bess-btn-primary {
          background-color: #006194 !important;
          color: #ffffff !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          border: none !important;
          border-radius: 8px !important;
          height: 44px !important;
          cursor: pointer !important;
          transition: background-color 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-primary:hover {
          background-color: #007bb9 !important;
        }

        .bess-btn-primary:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }

        .bess-btn-secondary {
          background-color: #f2f4f6 !important;
          color: #191c1e !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 500 !important;
          font-size: 14px !important;
          border: 1px solid #e6e8ea !important;
          border-radius: 8px !important;
          height: 44px !important;
          cursor: pointer !important;
          transition: all 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-secondary:hover {
          background-color: #e6e8ea !important;
        }

        /* Upload zone */
        .bess-upload-zone {
          border: 2px dashed #bfc7d2 !important;
          border-radius: 10px !important;
          padding: 32px !important;
          background-color: #ffffff !important;
          text-align: center !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease-in-out !important;
          cursor: pointer !important;
        }

        .bess-upload-zone.active {
          border-color: #006194 !important;
          background-color: rgba(219, 234, 254, 0.2) !important;
        }

        .bess-upload-zone:hover {
          border-color: #006194 !important;
        }

        /* Download template button green */
        .bess-btn-download {
          background-color: #6cf8bb !important;
          color: #00714d !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          border: none !important;
          border-radius: 8px !important;
          height: 38px !important;
          padding: 0 16px !important;
          cursor: pointer !important;
          transition: opacity 0.2s !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .bess-btn-download:hover {
          opacity: 0.9 !important;
        }

        /* Technical unit badges */
        .tech-badge {
          font-family: 'Geist', monospace !important;
          font-size: 10px !important;
          font-weight: 600 !important;
          background-color: #f2f4f6 !important;
          color: #707881 !important;
          padding: 2px 6px !important;
          border-radius: 4px !important;
          text-transform: uppercase !important;
          display: inline-block !important;
          margin-left: 6px !important;
        }

        /* Table styles */
        .bess-table {
          width: 100% !important;
          border-collapse: collapse !important;
        }

        .bess-table th {
          background-color: #f8fafc !important;
          font-family: 'Geist', sans-serif !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          color: #707881 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          padding: 12px 16px !important;
          border-bottom: 1px solid #e6e8ea !important;
          text-align: left !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }

        .bess-table td {
          padding: 14px 16px !important;
          border-bottom: 1px solid #f2f4f6 !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .bess-table tr:hover td {
          background-color: rgba(248, 250, 252, 0.5) !important;
        }

        .bess-table-actions {
          display: flex !important;
          gap: 12px !important;
          justify-content: center !important;
        }

        .bess-table th.text-center,
        .bess-table td.text-center {
          text-align: center !important;
        }

        .bess-action-link {
          color: #006194 !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          font-size: 13px !important;
          text-decoration: none !important;
          background: none !important;
          border: none !important;
          padding: 0 !important;
        }

        .bess-action-link:hover {
          color: #007bb9 !important;
          text-decoration: underline !important;
        }

        .bess-action-link.secondary {
          color: #707881 !important;
        }

        .bess-action-link.secondary:hover {
          color: #191c1e !important;
        }

        .bess-action-link.danger {
          color: #dc2626 !important;
        }

        .bess-action-link.danger:hover {
          color: #b91c1c !important;
          text-decoration: underline !important;
        }

        /* Geist font alignment for data values */
        .geist-val {
          font-family: 'Geist', monospace !important;
          font-weight: 500 !important;
        }
      `}</style>

      <div className="max-w-[1440px] mx-auto w-full flex-1">
        <Tabs
          defaultActiveKey="pcs"
          items={tabItems}
          className="premium-tabs"
        />
      </div>
    </div>
  );
}

// ==========================================
// 1. PCS Tab Component
// ==========================================
function PcsTab({ pcsList, loading, onSuccess }) {
  const { message, modal } = App.useApp();

  // Manual Form State
  const [oem, setOem] = useState("");
  const [model, setModel] = useState("");
  const [auxOp, setAuxOp] = useState("");
  const [auxStby, setAuxStby] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  // Excel Upload State
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [savingExcel, setSavingExcel] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editOem, setEditOem] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editAuxOp, setEditAuxOp] = useState("");
  const [editAuxStby, setEditAuxStby] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!oem.trim() || !model.trim() || !auxOp || !auxStby) {
      message.error("Please fill in all fields.");
      return;
    }
    const opVal = parseFloat(auxOp);
    const stbyVal = parseFloat(auxStby);
    if (isNaN(opVal) || opVal < 0 || isNaN(stbyVal) || stbyVal < 0) {
      message.error("Power consumption values must be positive numbers.");
      return;
    }

    setSavingManual(true);
    try {
      await createPCS({
        oem: oem.trim(),
        model: model.trim(),
        aux_power_operation: opVal,
        aux_power_standby: stbyVal,
      });
      message.success("PCS System saved successfully!");
      setOem("");
      setModel("");
      setAuxOp("");
      setAuxStby("");
      if (onSuccess) onSuccess();
    } catch (err) {
      message.error(err.message || "Failed to save PCS system.");
    } finally {
      setSavingManual(false);
    }
  };

  const handleDownloadTemplate = () => {
    const wsData = [
      ["oem", "model", "aux_power_operation", "aux_power_standby"],
      ["PCS_OEM_A", "PCS_Model_A", 15.0, 250.0],
      ["PCS_OEM_B", "PCS_Model_B", 18.5, 300.0],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PCS Template");
    XLSX.writeFile(wb, "pcs_template.xlsx");
    message.success("Excel Template downloaded successfully.");
  };

  const handleFileParse = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      message.error("Invalid file format. Please upload an Excel (.xlsx or .xls) file.");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          throw new Error("Excel sheet is empty or contains no records.");
        }

        const headers = jsonData[0].map((h) => String(h || "").trim().toLowerCase());
        const expectedHeaders = ["oem", "model", "aux_power_operation", "aux_power_standby"];
        const missing = expectedHeaders.filter((h) => !headers.includes(h));
        if (missing.length > 0) {
          throw new Error(`Excel is missing required columns: ${missing.join(", ")}`);
        }

        const oemIdx = headers.indexOf("oem");
        const modelIdx = headers.indexOf("model");
        const opIdx = headers.indexOf("aux_power_operation");
        const standbyIdx = headers.indexOf("aux_power_standby");

        const items = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0 || row.every((cell) => cell === null || cell === undefined || String(cell).trim() === "")) {
            continue;
          }

          const opVal = parseFloat(row[opIdx]);
          const standbyVal = parseFloat(row[standbyIdx]);

          if (isNaN(opVal) || isNaN(standbyVal)) {
            throw new Error(`Row ${i + 1} has invalid numbers.`);
          }

          items.push({
            oem: String(row[oemIdx]).trim(),
            model: String(row[modelIdx]).trim(),
            aux_power_operation: opVal,
            aux_power_standby: standbyVal,
          });
        }

        setParsedData(items);
        message.success(`Successfully parsed ${items.length} records.`);
      } catch (err) {
        message.error(`Parsing failed: ${err.message}`);
        setParsedData([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveExcel = async () => {
    setSavingExcel(true);
    let successCount = 0;
    try {
      for (const item of parsedData) {
        await createPCS(item);
        successCount++;
      }
      message.success(`Successfully registered ${successCount} new PCS profiles!`);
      setParsedData([]);
      setFileName("");
      if (onSuccess) onSuccess();
    } catch (err) {
      message.error(err.message || "Failed to save profiles.");
    } finally {
      setSavingExcel(false);
    }
  };

  const startEdit = (pcs) => {
    setEditingId(pcs.id);
    setEditOem(pcs.oem);
    setEditModel(pcs.model);
    setEditAuxOp(pcs.aux_power_operation);
    setEditAuxStby(pcs.aux_power_standby);
  };

  const handleSaveEdit = async (id) => {
    if (!editOem.trim() || !editModel.trim() || !editAuxOp || !editAuxStby) {
      message.error("Fields cannot be empty.");
      return;
    }
    const opVal = parseFloat(editAuxOp);
    const stbyVal = parseFloat(editAuxStby);
    if (isNaN(opVal) || opVal < 0 || isNaN(stbyVal) || stbyVal < 0) {
      message.error("Values must be positive numbers.");
      return;
    }

    setSavingEdit(true);
    try {
      const res = await customFetch(`${BASE_URL}/static/pcs/${id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          oem: editOem.trim(),
          model: editModel.trim(),
          aux_power_operation: opVal,
          aux_power_standby: stbyVal
        })
      });
      if (!res.ok) {
        throw new Error("Failed to update PCS profile");
      }
      message.success("PCS profile updated successfully!");
      setEditingId(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      message.error(err.message || "Failed to update PCS profile.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePCS = (pcs) => {
    const displayName = `${pcs.oem || ''} - ${pcs.model || ''}`.trim() || `PCS #${pcs.id}`;
    modal.confirm({
      title: "Delete PCS System?",
      content: `Are you sure you want to delete PCS "${displayName}"? This action will also delete associated output power data and cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePCS(pcs.id);
          message.success("PCS System deleted successfully!");
          if (onSuccess) onSuccess();
        } catch (err) {
          message.error(err.message || "Failed to delete PCS system.");
        }
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 animate-fade-in items-stretch">
      {/* Row 1: Left Form, Right Drag & Drop */}
      <div className="lg:col-span-6 flex flex-col h-full">
        <div className="bess-card flex-1">
          <h3 className="headline-sm font-semibold text-slate-800 border-b border-slate-100 pb-3 flex items-center gap-1.5">
            Register New PCS
          </h3>
          <form onSubmit={handleManualSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">OEM Manufacturer</label>
                <input
                  type="text"
                  required
                  value={oem}
                  onChange={(e) => setOem(e.target.value)}
                  placeholder="e.g. SMA, Kehua"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Model Identifier</label>
                <input
                  type="text"
                  required
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. SC3150"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600 flex items-center">
                  Aux Power Operation
                  <span className="tech-badge">KW</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  required
                  value={auxOp}
                  onChange={(e) => setAuxOp(e.target.value)}
                  placeholder="e.g. 15.0"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600 flex items-center">
                  Aux Power Standby
                  <span className="tech-badge">W</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={auxStby}
                  onChange={(e) => setAuxStby(e.target.value)}
                  placeholder="e.g. 250"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingManual}
              className="w-full bess-btn-primary mt-2"
            >
              {savingManual ? <LoadingOutlined /> : <PlusOutlined />} Save Configuration
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-6 flex flex-col h-full">
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileParse(e.dataTransfer.files[0]); }}
          className={`bess-upload-zone flex-1 ${dragActive ? "active" : ""}`}
        >
          <div className="w-14 h-14 bg-[#f0f9ff] rounded-full flex items-center justify-center mb-4">
            <UploadOutlined className="text-2xl text-[#006194]" />
          </div>
          <h3 className="headline-sm font-semibold text-slate-800 mb-1">
            Bulk Configuration Import
          </h3>
          <p className="text-xs text-slate-500 max-w-sm text-center mb-5 leading-relaxed">
            Drag and drop your Excel configuration files here or click to browse files from your local system.
          </p>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => { if (e.target.files?.[0]) handleFileParse(e.target.files[0]); }}
            className="hidden"
            id="pcs-file-upload-input"
          />
          
          <div className="flex flex-col items-center gap-3">
            <label
              htmlFor="pcs-file-upload-input"
              className="h-10 px-4 bg-white hover:text-slate-400 text-xs font-semibold text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0 !important' }}
            >
              Browse Files
            </label>

            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bess-btn-download"
            >
              <FileTextOutlined /> Download Template
            </button>
          </div>

          {fileName && (
            <div className="mt-3 text-xs text-slate-600 font-medium">
              Selected: <span className="text-[#006194]">{fileName}</span>
            </div>
          )}

          {parsedData.length > 0 && (
            <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/50 mt-4 w-full max-w-md">
              <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 mb-2">
                <span>Total Records</span>
                <span>{parsedData.length} records</span>
              </div>
              <button
                onClick={handleSaveExcel}
                disabled={savingExcel}
                className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer flex justify-center items-center gap-1.5"
              >
                {savingExcel ? <LoadingOutlined /> : <CheckCircleOutlined />} Save excel records ({parsedData.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Bottom Table */}
      <div className="lg:col-span-12 mt-2">
        <div className="bess-card flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
            <h3 className="headline-sm font-semibold text-slate-800">
              Registered PCS Systems
            </h3>
          </div>

          {loading ? (
            <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs">
              <LoadingOutlined className="text-xl mb-2 text-[#006194] animate-spin" />
              <div>Loading PCS systems...</div>
            </div>
          ) : pcsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="bess-table">
                <thead>
                  <tr>
                    <th style={{ width: '25%' }}>OEM</th>
                    <th style={{ width: '25%' }}>Model</th>
                    <th style={{ width: '20%' }}>Op (kW)</th>
                    <th style={{ width: '20%' }}>Standby (W)</th>
                    <th className="text-center" style={{ width: '10%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pcsList.map((pcs) => (
                    editingId === pcs.id ? (
                      <tr key={pcs.id}>
                        <td>
                          <input
                            type="text"
                            required
                            value={editOem}
                            onChange={(e) => setEditOem(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            required
                            value={editModel}
                            onChange={(e) => setEditModel(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.001"
                            required
                            value={editAuxOp}
                            onChange={(e) => setEditAuxOp(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            required
                            value={editAuxStby}
                            onChange={(e) => setEditAuxStby(e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleSaveEdit(pcs.id)}
                              disabled={savingEdit}
                              className="bess-action-link"
                            >
                              {savingEdit ? "Saving" : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bess-action-link secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={pcs.id}>
                        <td className="font-semibold text-slate-700">{pcs.oem}</td>
                        <td className="text-slate-600">{pcs.model}</td>
                        <td className="geist-val">{parseFloat(pcs.aux_power_operation).toFixed(1)} kW</td>
                        <td className="geist-val">{parseFloat(pcs.aux_power_standby).toFixed(1)} W</td>
                        <td className="text-center">
                          <div className="bess-table-actions">
                            <button onClick={() => startEdit(pcs)} className="bess-action-link">
                              Edit
                            </button>
                            <button onClick={() => handleDeletePCS(pcs)} className="bess-action-link danger">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12 text-center text-slate-400 text-xs italic">
              No PCS systems registered. Please add manually or upload.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// 2. PCS Output Power Tab Component
// ==========================================
function PcsOutputTab({ pcsList, loadingPcs }) {
  const { message, modal } = App.useApp();
  const [selectedPcs, setSelectedPcs] = useState("");
  const [outputs, setOutputs] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Manual Form Inputs
  const [ambientTemp, setAmbientTemp] = useState("");
  const [acOutput, setAcOutput] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  // Inline Edit State
  const [editingId, setEditingId] = useState(null);
  const [editTemp, setEditTemp] = useState("");
  const [editAcOutput, setEditAcOutput] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Excel Upload State
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [savingExcel, setSavingExcel] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const loadOutputs = async (pcsId) => {
    if (!pcsId) {
      setOutputs([]);
      return;
    }
    try {
      setLoadingList(true);
      const data = await fetchPCSOutputPowers(pcsId);
      data.sort((a, b) => a.ambient_temp_c - b.ambient_temp_c);
      setOutputs(data);
    } catch (err) {
      message.error("Failed to load output power profile: " + err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (pcsList.length > 0 && !selectedPcs) {
      setSelectedPcs(pcsList[0].id);
      loadOutputs(pcsList[0].id);
    }
  }, [pcsList]);

  const handlePcsChange = (val) => {
    setSelectedPcs(val);
    loadOutputs(val);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPcs) {
      message.error("Please select a PCS Model first.");
      return;
    }
    if (ambientTemp === "" || !acOutput) {
      message.error("Please fill in all fields.");
      return;
    }

    setSavingManual(true);
    try {
      await createPCSOutputPower({
        pcs: Number(selectedPcs),
        ambient_temp_c: parseFloat(ambientTemp),
        ac_output_kva: parseFloat(acOutput),
      });
      message.success("PCS Output Power row saved successfully!");
      setAmbientTemp("");
      setAcOutput("");
      loadOutputs(selectedPcs);
    } catch (err) {
      message.error(err.message || "Failed to save output power row.");
    } finally {
      setSavingManual(false);
    }
  };

  const handleDownloadTemplate = () => {
    const wsData = [
      ["ambient_temp_c", "ac_output_kva"],
      [25.0, 3150.0],
      [35.0, 2900.0],
      [45.0, 2600.0],
      [50.0, 2400.0],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PCS Output Template");
    XLSX.writeFile(wb, "pcs_output_power_template.xlsx");
    message.success("Excel Template downloaded successfully.");
  };

  const handleFileParse = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      message.error("Invalid file format. Please upload an Excel (.xlsx or .xls) file.");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          throw new Error("Excel sheet is empty.");
        }

        const cleanedHeaders = jsonData[0].map((h) => String(h || "").trim().toLowerCase().replace(/[\%\(\)]/g, "").trim());
        const tempIdx = cleanedHeaders.findIndex(h => h.includes("temp") || h.includes("ambient"));
        const acIdx = cleanedHeaders.findIndex(h => h.includes("kva") || h.includes("ac_output") || h.includes("output"));

        const missing = [];
        if (tempIdx === -1) missing.push("ambient_temp_c");
        if (acIdx === -1) missing.push("ac_output_kva");

        if (missing.length > 0) {
          throw new Error(`Excel is missing columns: ${missing.join(", ")}`);
        }

        const items = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0 || row.every((c) => c === null || c === undefined || String(c).trim() === "")) continue;

          items.push({
            ambient_temp_c: parseFloat(row[tempIdx]),
            ac_output_kva: parseFloat(row[acIdx]),
          });
        }

        setParsedData(items);
        message.success(`Successfully parsed ${items.length} records.`);
      } catch (err) {
        message.error(`Parsing failed: ${err.message}`);
        setParsedData([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveExcel = async () => {
    if (!selectedPcs) {
      message.error("Please select a PCS Model.");
      return;
    }
    setSavingExcel(true);
    try {
      const file = new File(
        [
          (() => {
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(parsedData);
            XLSX.utils.book_append_sheet(wb, ws, "sheet1");
            return XLSX.write(wb, { bookType: "xlsx", type: "array" });
          })(),
        ],
        "file.xlsx"
      );

      await uploadPcsOutputPower(selectedPcs, file);
      message.success("Successfully uploaded PCS Output Power profile!");
      setParsedData([]);
      setFileName("");
      loadOutputs(selectedPcs);
    } catch (err) {
      message.error(err.message || "Failed to upload output power profile.");
    } finally {
      setSavingExcel(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditTemp(r.ambient_temp_c);
    setEditAcOutput(r.ac_output_kva);
  };

  const handleSaveEdit = async (id) => {
    if (editTemp === "" || editAcOutput === "") {
      message.error("Please fill in all fields.");
      return;
    }
    const tempVal = parseFloat(editTemp);
    const acVal = parseFloat(editAcOutput);
    if (isNaN(tempVal) || isNaN(acVal) || acVal < 0) {
      message.error("Invalid input values.");
      return;
    }

    setSavingEdit(true);
    try {
      await updatePCSOutputPower(id, {
        pcs: selectedPcs,
        ambient_temp_c: tempVal,
        ac_output_kva: acVal,
      });
      message.success("PCS Output Power row updated successfully!");
      setEditingId(null);
      loadOutputs(selectedPcs);
    } catch (err) {
      message.error(err.message || "Failed to update PCS output power row.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteOutputRow = (r) => {
    modal.confirm({
      title: "Delete Output Power Row?",
      content: `Are you sure you want to delete row for ${parseFloat(r.ambient_temp_c).toFixed(1)}°C (${parseFloat(r.ac_output_kva).toFixed(2)} kVA)?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePCSOutputPower(r.id);
          message.success("Output power row deleted successfully!");
          loadOutputs(selectedPcs);
        } catch (err) {
          message.error(err.message || "Failed to delete output power row.");
        }
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 animate-fade-in items-stretch">
      {/* Left List & Selector */}
      <div className="lg:col-span-7 flex flex-col h-full">
        <div className="bess-card flex-1">
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-3 gap-3 mb-4">
            <h3 className="headline-sm font-semibold text-slate-800">
              PCS Temperature Derating Profile
            </h3>
            <Select
              value={selectedPcs || undefined}
              disabled={loadingPcs}
              onChange={(val) => handlePcsChange(val)}
              placeholder="Select PCS Model..."
              className="bess-antd-select max-w-xs w-full"
              style={{ width: '100%', maxWidth: '320px' }}
            >
              {pcsList.map((pcs) => (
                <Select.Option key={pcs.id} value={pcs.id}>
                  {pcs.oem} - {pcs.model}
                </Select.Option>
              ))}
            </Select>
          </div>

          {loadingList ? (
            <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs">
              <LoadingOutlined className="text-xl mb-2 text-[#006194] animate-spin" />
              <div>Loading output curves...</div>
            </div>
          ) : outputs.length > 0 ? (
            <div className="overflow-x-auto max-h-[850px]">
              <table className="bess-table">
                <thead>
                  <tr>
                    <th>Ambient Temp (°C)</th>
                    <th style={{ textAlign: 'right' }}>AC Output Capacity (kVA)</th>
                    <th className="text-center" style={{ width: '130px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {outputs.map((r) => (
                    editingId === r.id ? (
                      <tr key={r.id}>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            required
                            value={editTemp}
                            onChange={(e) => setEditTemp(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            required
                            value={editAcOutput}
                            onChange={(e) => setEditAcOutput(e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleSaveEdit(r.id)}
                              disabled={savingEdit}
                              className="bess-action-link"
                            >
                              {savingEdit ? "Saving" : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bess-action-link secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={r.id}>
                        <td className="font-semibold text-slate-700">{parseFloat(r.ambient_temp_c).toFixed(1)} °C</td>
                        <td className="text-right geist-val font-semibold text-slate-800">{parseFloat(r.ac_output_kva).toFixed(2)} kVA</td>
                        <td className="text-center">
                          <div className="bess-table-actions">
                            <button onClick={() => startEdit(r)} className="bess-action-link">
                              Edit
                            </button>
                            <button onClick={() => handleDeleteOutputRow(r)} className="bess-action-link danger">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12 text-center text-slate-400 text-xs italic">
              No temperature derating profiles registered for selected PCS model. Add records manually or upload Excel.
            </div>
          )}
        </div>
      </div>

      {/* Right Form & Upload (Swapped so upload box is on top) */}
      <div className="lg:col-span-5 flex flex-col gap-6">
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileParse(e.dataTransfer.files[0]); }}
          className={`bess-upload-zone ${dragActive ? "active" : ""}`}
        >
          <UploadOutlined className="text-2xl mb-2 text-[#006194]" />
          <h4 className="font-semibold text-slate-800 mb-1 text-sm">
            Bulk Upload PCS Output Power
          </h4>
          <p className="text-xs text-slate-500 mb-4 max-w-xs leading-relaxed">
            Drag & Drop Profile Excel here or select browse
          </p>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => { if (e.target.files?.[0]) handleFileParse(e.target.files[0]); }}
            className="hidden"
            id="output-file-upload-input"
          />
          
          <div className="flex flex-col items-center gap-2">
            <label
              htmlFor="output-file-upload-input"
              className="h-9 px-3 bg-white hover:text-slate-400 border border-slate-200 rounded text-xs font-semibold text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
              style={{ marginBottom: '0 !important' }}
            >
              Browse Files
            </label>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bess-btn-download h-8 text-[11px]"
            >
              <FileTextOutlined /> Download Template
            </button>
          </div>

          {fileName && (
            <div className="mt-2 text-xs text-slate-600">
              Selected: <span className="text-[#006194]">{fileName}</span>
            </div>
          )}

          {parsedData.length > 0 && (
            <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/50 mt-3 w-full">
              <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 mb-2">
                <span>Total Records</span>
                <span>{parsedData.length} records</span>
              </div>
              <button
                onClick={handleSaveExcel}
                disabled={savingExcel}
                className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold transition-colors cursor-pointer flex justify-center items-center gap-1.5"
              >
                {savingExcel ? <LoadingOutlined /> : <CheckCircleOutlined />} Save Curve
              </button>
            </div>
          )}
        </div>

        <div className="bess-card">
          <h3 className="headline-sm font-semibold text-slate-800 border-b border-slate-100 pb-3 flex items-center gap-1.5">
            Add Output Power Row
          </h3>
          <form onSubmit={handleManualSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Ambient Temperature (°C)</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={ambientTemp}
                  onChange={(e) => setAmbientTemp(e.target.value)}
                  placeholder="e.g. 25"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600 flex items-center">
                  AC Output Capacity
                  <span className="tech-badge">KVA</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={acOutput}
                  onChange={(e) => setAcOutput(e.target.value)}
                  placeholder="e.g. 3150"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingManual}
              className="w-full bess-btn-primary mt-2"
            >
              {savingManual ? <LoadingOutlined /> : <PlusOutlined />} Add Output Row
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
